/**
 * Le contenu de jeu (familles, monstres, items, recettes) est une DONNÉE,
 * jamais un import du moteur : `public/contenu.json` est servi comme asset
 * statique et injecté dans la simulation.
 *
 * C'est ce qui garde `src/sim/` pur (GDD §14.2) : le moteur ne va jamais
 * chercher quoi que ce soit, on le lui donne.
 */

export interface Palier {
  palier: number
  niveau: number
  pv: number
  res: { terre: number; feu: number; eau: number; air: number; neutre: number }
  stats: { force: number; intelligence: number; chance: number; agilite: number }
}

export interface Butin {
  item: number
  /** taux en % par palier de monstre (1 à 5) */
  taux: number[]
  conditionnel: boolean
}

export interface Monstre {
  id: number
  nom: string
  slug: string
  famille: number
  paliers: Palier[]
  butin: Butin[]
}

export interface Famille {
  id: number
  nom: string
  slug: string
  monstres: number[]
  niveauMin: number
  niveauMax: number
  /** zone d'appartenance ; null quand le dump ne la renseigne pas */
  zone?: number | null
}

export interface Zone {
  id: number
  nom: string
  slug: string
  niveauMin: number
  niveauMax: number
  /** coût de déblocage en kamas ; 0 pour la zone de départ */
  cout: number
}

export interface Item {
  id: number
  nom: string
  niveau: number
  type: string
  panoplie: number | null
  effets: { stat: string; effet: number; min: number; max: number }[]
  prix: number
}

export interface Panoplie {
  id: number
  nom: string
  items: number[]
  /** nombre de pièces portées → effets accordés */
  paliers?: Record<string, { effet: number; valeur: number }[]>
}

export interface Recolte {
  item: number
  /** id du métier (jobs.json) */
  metier: number
  /** niveau de métier requis */
  niveauRequis: number
}

export interface Contenu {
  /** identifiant d'effet → libellé, pour énoncer les bonus de panoplie */
  stats: Record<string, string>
  familles: Record<string, Famille>
  monstres: Record<string, Monstre>
  items: Record<string, Item>
  recettes: { resultat: number; niveau: number; ingredients: { item: number; qte: number }[] }[]
  panoplies: Record<string, Panoplie>
  recoltes: Record<string, Recolte>
  zones: Record<string, Zone>
  /** avis de recherche : id → niveau (GDD §10) */
  recherches: Record<string, { id: number; niveau: number; zone?: number | null }>
  /** archimonstres : id → niveau */
  archimonstres: Record<string, { id: number; niveau: number; zone?: number | null }>
  donjons: Record<string, import("./donjon.ts").Donjon>
  /** id de métier → nom et identifiant d'icône (img/job est indexé par icône) */
  metiers: Record<string, { nom: string; icone: number }>
}

export const famille = (c: Contenu, id: number) => c.familles[String(id)]
export const monstre = (c: Contenu, id: number) => c.monstres[String(id)]
export const item = (c: Contenu, id: number) => c.items[String(id)]
export const recolte = (c: Contenu, id: number) => c.recoltes[String(id)]
export const zone = (c: Contenu, id: number) => c.zones[String(id)]
export const donjon = (c: Contenu, id: number) => c.donjons[String(id)]
